// Dependency-free control pane. Implements the
// TPaneLike / TFolderLike / TBindingLike contract every pane builder funnels
// through (../../panel/types), so the builders, the slider bridge
// (sliderTicks → rangeAdapter), and the rail controller keep working unchanged:
// we swap the implementation behind `new Pane()`, not the call sites.
//
// DOM mirrors the subset of the pane structure the app actually touches, so
// the ported theme CSS, the slice label-cell hijack, and the section show/hide
// rules all match:
//
//   .mz-rotv > .mz-rotv_c > .mz-fldv[data-section]
//                             > .mz-fldv_b (collapse bar) + .mz-fldv_c (body)
//                                 > .mz-lblv (row) > .mz-lblv_l + .mz-lblv_v
//
// A numeric/interval binding leaves its value cell empty — the external
// RangeControl mount (rangeAdapter.mountRangeControl) fills it. Pure DOM; lifts
// into webpic. (Phase B renames the `.mz-*` classes.)

import { createCheckbox } from './checkbox';
import { createSelect } from './select';
import { createTextInput } from './textInput';
import type { TBindingLike, TFolderLike, TPaneLike } from '../../panel/types';

/** Anything a folder owns and disposes on rebuild. */
interface Child {
    element?: HTMLElement;
    dispose?: () => void;
    refresh?: () => void;
}

/** Return shape of `Folder.addButton` (mirrors the TFolderLike button handle). */
interface PaneButton {
    element: HTMLElement;
    on(ev: string, cb: () => void): PaneButton;
    disabled: boolean;
    dispose(): void;
}

interface Widget {
    setDisplay(value: unknown): void;
    setDisabled(disabled: boolean): void;
    dispose(): void;
}

function div(cls: string): HTMLDivElement {
    const d = document.createElement('div');
    d.className = cls;
    return d;
}

class Binding implements TBindingLike {
    element: HTMLElement;
    private valueCell: HTMLElement;
    private listeners: Array<(e: { value: unknown }) => void> = [];
    private widget: Widget | null;
    private target: Record<string, unknown>;
    private key: string;
    private _disabled = false;

    constructor(target: Record<string, unknown>, key: string, opts: Record<string, unknown>) {
        this.target = target;
        this.key = key;
        this.element = div('mz-lblv');
        const labelCell = div('mz-lblv_l');
        labelCell.textContent = String(opts['label'] ?? key);
        this.valueCell = div('mz-lblv_v');
        this.element.append(labelCell, this.valueCell);
        this.widget = this.buildWidget(opts);
    }

    /** Write-through to the bound object (two-way binding semantics), then fan
     *  out to change listeners. Numeric sliders own their value via the external
     *  RangeControl + sliderTicks, so this fires only for checkbox/select/text. */
    private commit(value: unknown): void {
        this.target[this.key] = value;
        for (const l of this.listeners) l({ value });
    }

    private buildWidget(opts: Record<string, unknown>): Widget | null {
        const value = this.target[this.key];
        // Interval ({min,max} object) or any numeric slider → empty value cell;
        // the external RangeControl fills it. Object check precedes primitives.
        if (opts['view'] === 'interval' || (value !== null && typeof value === 'object')) return null;
        if (opts['options'] !== undefined) {
            const select = createSelect({
                options: opts['options'] as Record<string, unknown>,
                getValue: () => this.target[this.key],
                onChange: (v) => this.commit(v),
            });
            this.valueCell.appendChild(select.el);
            return { setDisplay: () => select.sync(), setDisabled: (d) => select.setDisabled(d), dispose: () => select.dispose() };
        }
        if (typeof value === 'boolean') {
            const checkbox = createCheckbox({
                checked: value,
                ...(typeof opts['label'] === 'string' ? { ariaLabel: opts['label'] } : {}),
                onChange: (checked) => this.commit(checked),
            });
            this.valueCell.appendChild(checkbox.el);
            return { setDisplay: (v) => checkbox.set(!!v), setDisabled: (d) => checkbox.setDisabled(d), dispose: () => checkbox.dispose() };
        }
        // Slider (min/max) or bare number → empty value cell for RangeControl.
        if ((typeof opts['min'] === 'number' && typeof opts['max'] === 'number') || typeof value === 'number') return null;
        const text = createTextInput({
            value: value === undefined ? '' : String(value),
            onChange: (v) => this.commit(v),
        });
        this.valueCell.appendChild(text.el);
        return { setDisplay: (v) => text.set(v === undefined ? '' : String(v)), setDisabled: (d) => text.setDisabled(d), dispose: () => text.dispose() };
    }

    on(ev: string, cb: (e: { value: unknown }) => void): TBindingLike {
        if (ev === 'change') this.listeners.push(cb);
        return this;
    }

    get disabled(): boolean { return this._disabled; }
    set disabled(v: boolean) {
        this._disabled = v;
        this.element.classList.toggle('is-disabled', v);
        this.widget?.setDisabled(v);
    }

    refresh(): void { this.widget?.setDisplay(this.target[this.key]); }

    dispose(): void {
        this.widget?.dispose();
        this.element.remove();
    }
}

class Folder implements TFolderLike {
    element: HTMLElement;
    /** Bindings / sub-folders / blades / buttons added through this folder. Raw
     *  footer DOM (appendChild'd by builders) is deliberately NOT here, so the
     *  rebuild dispose-loop only clears the controls. */
    children: Child[] = [];
    title?: string;
    private body: HTMLElement;

    constructor(opts: { title: string; expanded?: boolean }) {
        this.title = opts.title;
        this.element = div('mz-fldv');
        const bar = document.createElement('button');
        bar.type = 'button';
        bar.className = 'mz-fldv_b';
        const titleEl = div('mz-fldv_t');
        titleEl.textContent = opts.title;
        bar.appendChild(titleEl);
        this.body = div('mz-fldv_c');
        // Sections mount expanded with the bar hidden via CSS; honour the flag
        // anyway for any folder that does show its header.
        bar.addEventListener('click', () => { this.body.hidden = !this.body.hidden; });
        if (opts.expanded === false) this.body.hidden = true;
        this.element.append(bar, this.body);
    }

    /** Register a child so `dispose()` self-removes it from `children` — matches
     *  the convention where disposing a child shrinks `folder.children`. */
    private attach<C extends Child>(child: C): C {
        const own = child.dispose?.bind(child);
        child.dispose = (): void => {
            own?.();
            const i = this.children.indexOf(child);
            if (i >= 0) this.children.splice(i, 1);
        };
        this.children.push(child);
        return child;
    }

    addBinding<T extends object, K extends keyof T>(target: T, key: K, opts: Record<string, unknown> = {}): TBindingLike {
        const binding = new Binding(target as Record<string, unknown>, key as string, opts);
        this.body.appendChild(binding.element);
        this.attach(binding as unknown as Child);
        return binding;
    }

    addFolder(opts: { title: string; expanded?: boolean }): TFolderLike {
        const folder = new Folder(opts);
        this.body.appendChild(folder.element);
        this.attach(folder as unknown as Child);
        return folder;
    }

    addBlade(opts: { view: string }): unknown {
        if (opts.view !== 'separator') return undefined;
        const sep = div('mz-sprv');
        sep.appendChild(div('mz-sprv_r'));
        this.body.appendChild(sep);
        return this.attach({ element: sep, dispose: () => sep.remove() });
    }

    addButton(opts: { title: string; disabled?: boolean }): { on: (ev: string, cb: () => void) => unknown; element?: HTMLElement; disabled?: boolean } {
        const wrap = div('mz-btnv');
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'mz-btnv_b';
        btn.disabled = !!opts.disabled;
        const label = div('mz-btnv_t');
        label.textContent = opts.title;
        btn.appendChild(label);
        wrap.appendChild(btn);
        const cbs: Array<() => void> = [];
        const onClick = (): void => { if (!btn.disabled) for (const c of cbs) c(); };
        btn.addEventListener('click', onClick);
        const handle: PaneButton = {
            element: wrap,
            on(ev: string, cb: () => void): PaneButton { if (ev === 'click') cbs.push(cb); return handle; },
            get disabled(): boolean { return btn.disabled; },
            set disabled(v: boolean) { btn.disabled = v; },
            dispose: (): void => { btn.removeEventListener('click', onClick); wrap.remove(); },
        };
        this.body.appendChild(wrap);
        this.attach(handle as unknown as Child);
        return handle;
    }

    refresh(): void { for (const c of this.children) c.refresh?.(); }

    dispose(): void {
        for (const c of this.children.slice()) c.dispose?.();
        this.element.remove();
    }
}

export class Pane implements TPaneLike {
    private rotv: HTMLElement;
    private rotvc: HTMLElement;
    private folders: Folder[] = [];

    constructor(opts: { container: HTMLElement }) {
        this.rotv = div('mz-rotv');
        this.rotvc = div('mz-rotv_c');
        this.rotv.appendChild(this.rotvc);
        opts.container.appendChild(this.rotv);
    }

    addFolder(opts: { title: string; expanded?: boolean }): TFolderLike {
        // Top-level section folders are reused across rebuilds (find-or-create
        // by the builders), never disposed individually — so they live here for
        // the pane's lifetime and `dispose()` tears them all down.
        const folder = new Folder(opts);
        this.rotvc.appendChild(folder.element);
        this.folders.push(folder);
        return folder;
    }

    refresh(): void { for (const f of this.folders) f.refresh(); }

    dispose(): void {
        for (const f of this.folders.slice()) f.dispose();
        this.folders.length = 0;
        this.rotv.remove();
    }
}
